'use client';

import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormLabel from '@c/forms/controls/FormLabel';
import FormTextarea from '@c/forms/controls/FormTextarea';
import BaseModal from '@c/modals/BaseModal';
import type { ChatDocument } from '@models/chat';
import {
  PaymentRequestDocument,
  PaymentRequestState,
} from '@models/payment-request';
import Button from '@ui/Button';
import { getNumber } from '@util/createListingHelpers';
import {
  createPaymentRequest,
  getPaymentRequestId,
} from '@util/firestore/payment-requests';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { useState } from 'react';

type RequestPaymentActionProps = {
  chat: ChatDocument;
};

export default function RequestPaymentAction({
  chat,
}: RequestPaymentActionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { showSuccessToast } = useToastContext();
  const { user } = useAuth();

  const close = () => {
    if (submitting) return;
    setIsOpen(false);
    setAmount(0);
    setDescription('');
    setError('');
  };

  const submit = async () => {
    if (!user?.uid) return;
    if (!amount || amount <= 0) {
      setError('Enter an amount greater than $0');
      return;
    }
    const payerUid =
      chat.buyer_id === user.uid ? chat.seller_id : chat.buyer_id;
    if (!payerUid) {
      setError('Could not determine who to charge in this chat.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const id = getPaymentRequestId();
      const request: PaymentRequestDocument = {
        id,
        chat_id: chat.id,
        requester_uid: user.uid,
        payer_uid: payerUid,
        amount,
        description: description.trim(),
        state: PaymentRequestState.Pending,
        created_at: Date.now(),
        paid_at: null,
        charge_id: null,
        failure_reason: null,
        order_id: chat.order_id ?? null,
      };
      await createPaymentRequest({ request, chat });
      showSuccessToast('Payment request sent');
      close();
    } catch (e) {
      logError(e);
      setError((e as Error)?.message ?? 'Failed to send payment request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        height="small"
        type="tertiary"
        width="small"
        text="Request Payment"
        onClick={() => setIsOpen(true)}
      />

      <BaseModal
        isOpen={isOpen}
        dismiss={close}
        title="Request Payment"
        disableBackdropDismiss={submitting}
      >
        <div className="flex w-[40rem] max-w-full flex-col gap-[2.4rem] p-8">
          <p className="text-[1.5rem] text-zinc-600">
            Send the customer a Stripe payment link in chat. They can pay with
            any saved card.
          </p>

          {error && (
            <div className="rounded-[1rem] bg-red-50 px-4 py-3 text-center text-[1.5rem] font-semibold text-red-600">
              {error}
            </div>
          )}

          <FormLabel value="Amount" required>
            <FormCurrencyInputFancy
              value={amount || ''}
              full
              allowEmpty
              onChange={(e) => setAmount(getNumber(e.target.value))}
            />
          </FormLabel>

          <FormLabel value="Description">
            <FormTextarea
              value={description}
              placeholder="Reason for charge (visible to the customer)"
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormLabel>

          <div className="flex items-center gap-4">
            <Button
              type="tertiary"
              width="fluid"
              text="Cancel"
              disabled={submitting}
              onClick={close}
            />
            <Button
              type="primary"
              width="fluid"
              text="Send Request"
              loading={submitting}
              disabled={!amount}
              onClick={submit}
            />
          </div>
        </div>
      </BaseModal>
    </>
  );
}
